import type {
  CanonicalTreeGeometryBranchRoot,
} from './CanonicalTreeGeometryBranchRootAuthority';

export type CanonicalTreeGeometryCompatibilitySide =
  | 'mother'
  | 'father'
  | 'self';

export type CanonicalTreeGeometryUnitDisposition =
  | 'viewer-owned'
  | 'single-member'
  | 'same-root'
  | 'blood-relative-owned'
  | 'both-side-explicit'
  | 'conflicting-roots-unresolved'
  | 'empty-unit-unresolved';

export interface CanonicalTreeGeometryUnitMember {
  readonly memberId:
    string;

  readonly root:
    CanonicalTreeGeometryBranchRoot;

  readonly layoutPath:
    string | null;
}

export interface CanonicalTreeGeometryUnitOwnershipInput {
  readonly members:
    readonly CanonicalTreeGeometryUnitMember[];

  readonly viewerMemberId?:
    string | null | undefined;
}

export interface CanonicalTreeGeometryUnitOwnershipResult {
  readonly root:
    CanonicalTreeGeometryBranchRoot;

  readonly compatibilitySide:
    CanonicalTreeGeometryCompatibilitySide;

  readonly layoutOwnerMemberId:
    string | null;

  readonly layoutPath:
    string | null;

  readonly disposition:
    CanonicalTreeGeometryUnitDisposition;

  readonly conflictingMemberIds:
    readonly string[];
}

const compatibilitySideOf = (
  root:
    CanonicalTreeGeometryBranchRoot,
): CanonicalTreeGeometryCompatibilitySide => {
  if (root === 'maternal') {
    return 'mother';
  }

  if (root === 'paternal') {
    return 'father';
  }

  return 'self';
};

const orderedMembers = (
  members:
    readonly CanonicalTreeGeometryUnitMember[],
): readonly CanonicalTreeGeometryUnitMember[] =>
  Object.freeze(
    [...members]
      .sort(
        (left, right) =>
          left.memberId.localeCompare(
            right.memberId,
          ),
      ),
  );

const result = ({
  root,
  layoutOwnerMemberId,
  layoutPath,
  disposition,
  conflictingMemberIds = [],
}: {
  readonly root:
    CanonicalTreeGeometryBranchRoot;

  readonly layoutOwnerMemberId:
    string | null;

  readonly layoutPath:
    string | null;

  readonly disposition:
    CanonicalTreeGeometryUnitDisposition;

  readonly conflictingMemberIds?:
    readonly string[];
}): CanonicalTreeGeometryUnitOwnershipResult =>
  Object.freeze({
    root,

    compatibilitySide:
      compatibilitySideOf(root),

    layoutOwnerMemberId,

    layoutPath,

    disposition,

    conflictingMemberIds:
      Object.freeze(
        [...conflictingMemberIds],
      ),
  });

const isBloodlineOrViewerRoot = (
  root:
    CanonicalTreeGeometryBranchRoot,
): boolean =>
  root === 'maternal' ||
  root === 'paternal' ||
  root === 'both' ||
  root === 'self' ||
  root === 'descendant';

export const resolveCanonicalTreeGeometryUnitOwnership = (
  input:
    CanonicalTreeGeometryUnitOwnershipInput,
): CanonicalTreeGeometryUnitOwnershipResult => {
  const members =
    orderedMembers(input.members);

  if (members.length === 0) {
    return result({
      root:
        'unresolved',

      layoutOwnerMemberId:
        null,

      layoutPath:
        null,

      disposition:
        'empty-unit-unresolved',
    });
  }

  const viewerMember =
    input.viewerMemberId
      ? members.find(
          member =>
            member.memberId ===
            input.viewerMemberId,
        ) ?? null
      : null;

  if (viewerMember) {
    return result({
      root:
        'self',

      layoutOwnerMemberId:
        viewerMember.memberId,

      layoutPath:
        viewerMember.layoutPath ??
        'self',

      disposition:
        'viewer-owned',
    });
  }

  if (members.length === 1) {
    const member =
      members[0];

    return result({
      root:
        member.root,

      layoutOwnerMemberId:
        member.memberId,

      layoutPath:
        member.layoutPath,

      disposition:
        'single-member',
    });
  }

  const roots =
    new Set(
      members.map(
        member =>
          member.root,
      ),
    );

  if (roots.size === 1) {
    const owner =
      members.find(
        member =>
          member.layoutPath !== null,
      ) ??
      members[0];

    return result({
      root:
        owner.root,

      layoutOwnerMemberId:
        owner.memberId,

      layoutPath:
        owner.layoutPath,

      disposition:
        'same-root',
    });
  }

  const bothMember =
    members.find(
      member =>
        member.root === 'both',
    );

  if (bothMember) {
    return result({
      root:
        'both',

      layoutOwnerMemberId:
        bothMember.memberId,

      layoutPath:
        bothMember.layoutPath,

      disposition:
        'both-side-explicit',
    });
  }

  const partnerAffiliatedMembers =
    members.filter(
      member =>
        member.root ===
        'partner-affiliated',
    );

  const bloodlineOwners =
    members.filter(
      member =>
        isBloodlineOrViewerRoot(
          member.root,
        ),
    );

  if (
    partnerAffiliatedMembers.length > 0 &&
    bloodlineOwners.length === 1 &&
    partnerAffiliatedMembers.length +
      bloodlineOwners.length ===
      members.length
  ) {
    const owner =
      bloodlineOwners[0];

    return result({
      root:
        owner.root,

      layoutOwnerMemberId:
        owner.memberId,

      layoutPath:
        owner.layoutPath,

      disposition:
        'blood-relative-owned',
    });
  }

  return result({
    root:
      'unresolved',

    layoutOwnerMemberId:
      null,

    layoutPath:
      null,

    disposition:
      'conflicting-roots-unresolved',

    conflictingMemberIds:
      members.map(
        member =>
          member.memberId,
      ),
  });
};
